import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Flex, Form, Input, Result, Space, Typography } from 'antd';
import { ColorSchemeToggle, MENSAGEM_DE_FALHA_INESPERADA, mensagemDeErro } from '@titularis/ui';
import { isAdminRole } from '@titularis/api-client';
import { api } from '../api';
import { signIn, signOut, useSession } from '../auth';

const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3333';

interface Campos {
  email: string;
  password: string;
}

/**
 * Consentimento do OAuth do MCP.
 *
 * É para cá que a API manda quem pediu autorização, e daqui a pessoa volta para
 * o `/mcp/authorize` com a sessão já criada. A tela é separada do `/entrar` por
 * duas razões: ela precisa dizer **o que** está sendo autorizado, e o `/entrar`
 * devolve para a home quem já está autenticado - o que, aqui, engoliria o
 * pedido no meio do caminho.
 *
 * **O botão é obrigatório, mesmo com sessão válida.** Conceder a um cliente de
 * linha de comando o direito de gravar no acervo é decisão, não passagem: um
 * redirecionamento automático faria a autorização acontecer sem que ninguém
 * tivesse lido o que estava concedendo.
 */
export function AutorizarPage() {
  const { data, isPending } = useSession();
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  /*
   * Os parâmetros vêm intactos da API e voltam intactos para ela: são o pedido
   * do OAuth (client_id, code_challenge, state), e reescrever qualquer um deles
   * invalidaria a autorização.
   */
  const parametros = new URLSearchParams(window.location.search);
  const clientId = parametros.get('client_id');
  /*
   * O `/auth` no meio não é enfeite: os endpoints do OAuth ficam sob o
   * `basePath` do BetterAuth, e é esse o endereço que a descoberta publica em
   * `authorization_endpoint`. Sem ele, o retorno cai em rota inexistente.
   */
  const destino = `${baseUrl}/auth/mcp/authorize?${parametros.toString()}`;

  /*
   * Quem é o cliente, para a tela poder dizer.
   *
   * **Até 11/09/2026 esta página mostrava o `client_id` e mais nada**, e o
   * registro dinâmico de cliente é aberto por desenho (D-52): qualquer um
   * registra um, e quem autoriza não tinha como distinguir o nosso de um
   * forjado. Autorizar às cegas não é decisão, é clique.
   */
  const cliente = useQuery({
    queryKey: ['mcp-client', clientId],
    queryFn: () => api.mcpClient(clientId!),
    enabled: Boolean(clientId) && Boolean(data?.user),
    retry: false,
  });

  /**
   * O endereço de retorno é nosso, ou de outra pessoa?
   *
   * É o campo que denuncia o cliente forjado: o token vai para onde ele mandar.
   * `localhost` entra porque é de lá que o Claude Code fala, e é o caso comum.
   *
   * **Avisa, e não bloqueia:** um cliente legítimo pode mudar de porta, e uma
   * tela que recusa por conta própria vira uma tela que se contorna por fora.
   */
  const enderecoConhecido = (url: string): boolean => {
    try {
      const { hostname } = new URL(url);
      return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === 'titularis.com.br' ||
        hostname.endsWith('.titularis.com.br')
      );
    } catch {
      return false;
    }
  };

  const retornos = cliente.data?.redirectUrls ?? [];
  const retornoEstranho = retornos.length > 0 && !retornos.every(enderecoConhecido);

  const entrar = async ({ email, password }: Campos) => {
    setErro(null);
    setEnviando(true);

    try {
      const { error } = await signIn.email({ email, password });

      if (error) {
        // As mesmas mensagens da tela de entrada: quem chega aqui é a mesma
        // pessoa, e a negativa de senha precisa apontar o Google do mesmo jeito.
        setErro(mensagemDeErro(error, 'E-mail ou senha incorretos.'));
      }
    } catch {
      setErro(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setEnviando(false);
    }
  };

  const moldura = (conteudo: React.ReactNode) => (
    <Flex align="center" justify="center" style={{ minHeight: '100dvh', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <Flex align="center" justify="space-between">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Titularis · Autorização
            </Typography.Title>
            <ColorSchemeToggle />
          </Flex>
          {conteudo}
        </Space>
      </div>
    </Flex>
  );

  if (isPending) return moldura(<Card loading />);

  /*
   * Sem `client_id` não há pedido nenhum: quem chegou aqui digitou o endereço,
   * e dizer isso é melhor do que mostrar um botão que autorizaria o vazio.
   */
  if (!clientId) {
    return moldura(
      <Card>
        <Result
          status="info"
          title="Nada para autorizar"
          subTitle="Esta página abre sozinha quando um cliente MCP pede acesso ao Titularis. Não há pedido em aberto."
        />
      </Card>,
    );
  }

  if (!data?.user) {
    return moldura(
      <Card>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            Entre com sua conta do backoffice para autorizar o acesso.
          </Typography.Text>

          {erro ? <Alert type="error" showIcon title={erro} /> : null}

          <Form<Campos> layout="vertical" onFinish={entrar} requiredMark={false} disabled={enviando}>
            <Form.Item
              name="email"
              label="E-mail"
              rules={[{ required: true, type: 'email', message: 'Informe um e-mail válido.' }]}
            >
              <Input autoComplete="email" size="large" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Senha"
              rules={[{ required: true, message: 'Informe sua senha.' }]}
            >
              <Input.Password autoComplete="current-password" size="large" />
            </Form.Item>

            <Button type="primary" htmlType="submit" size="large" block loading={enviando}>
              Entrar
            </Button>
          </Form>
        </Space>
      </Card>,
    );
  }

  if (!isAdminRole((data.user as { role?: string }).role)) {
    return moldura(
      <Card>
        <Result
          status="403"
          title="Acesso restrito"
          subTitle="Autorizar um cliente MCP é ação de quem publica questão. Sua conta não tem esse acesso."
          extra={
            <Button type="primary" onClick={() => void signOut()}>
              Sair
            </Button>
          }
        />
      </Card>,
    );
  }

  return moldura(
    <Card>
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph style={{ margin: 0 }}>
          Um cliente MCP está pedindo acesso ao Titularis em seu nome. Autorizado, ele poderá
          consultar o acervo e gravar comentários <strong>como sugestão</strong> - nada é
          publicado sem passar pela revisão de sempre.
        </Typography.Paragraph>

        <Descriptions
          size="small"
          column={1}
          items={[
            { key: 'conta', label: 'Sua conta', children: data.user.email },
            {
              key: 'cliente',
              label: 'Cliente',
              children: cliente.isPending ? (
                <Typography.Text type="secondary">carregando…</Typography.Text>
              ) : cliente.data ? (
                <Typography.Text strong>{cliente.data.name}</Typography.Text>
              ) : (
                <Typography.Text type="warning">não registrado</Typography.Text>
              ),
            },
            {
              key: 'retorno',
              label: 'Devolve o acesso para',
              children: retornos.length > 0 ? (
                <Space orientation="vertical" size={2}>
                  {retornos.map((url) => (
                    <Typography.Text key={url} code>
                      {url}
                    </Typography.Text>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
            },
            {
              key: 'id',
              label: 'Identificador',
              children: <Typography.Text code>{clientId}</Typography.Text>,
            },
          ]}
        />

        {retornoEstranho ? (
          <Alert
            type="warning"
            showIcon
            title="O acesso seria devolvido para fora dos nossos endereços"
            description="Se você não reconhece o endereço acima, feche esta página sem autorizar. Quem tem o endereço de retorno recebe o token."
          />
        ) : null}

        <Button type="primary" size="large" block href={destino}>
          Autorizar
        </Button>

        <Typography.Text type="secondary">
          Se você não iniciou este pedido, feche esta página sem autorizar.
        </Typography.Text>
      </Space>
    </Card>,
  );
}
